import { useQueryClient } from "@tanstack/react-query";
import { Tooltip } from "antd";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  BaselineListOptionsBar,
  IAddBaseline,
  IPatientBaseline,
  IPatientBaselineData,
} from "../../../../../../@types/Baselines";
import { Notification } from "../../../../../../@types/Notification";
import { Calendar } from "../../../../../../assets/icons/Calendar";
import { CircleX } from "../../../../../../assets/icons/CircleX";
import { GraphToggle } from "../../../../../../assets/icons/GraphToggle";
import { TableToggle } from "../../../../../../assets/icons/TableToggle";
import { Checkbox } from "../../../../../../components/Checkbox";
import { DateRangePicker } from "../../../../../../components/DateRangePicker";
import {
  OptionButton,
  OptionsBar,
} from "../../../../../../components/OptionsBar";
import { RadioButton } from "../../../../../../components/RadioButton";
import { TextField } from "../../../../../../components/TextField";
import {
  spirometryMeasurementsValuesFilters,
  spirometryQualityFilterTypes,
} from "../../../../../../constants/measurements";
import { useToast } from "../../../../../../hooks/useToast";
import { useUserPreferences } from "../../../../../../hooks/useUserPreferences";
import { baselinesService } from "../../../../../../services/baselinesService";
import {
  getLongFormattedDate,
  getShortFormattedDate,
} from "../../../../../../utils/dateFormatter";
import { mixpanelActions } from "../../../../../../utils/mixpanel";
import { ModalOptions } from "../OptionsPopover/Options.types";
import { ArchiveBaselineModal } from "./components/ArchiveBaselineModal";
import { ConfirmBaselineChangeModal } from "./components/ConfirmBaselineChangeModal";
import { ConfirmISHLTBaselineStatusChange } from "./components/ConfirmISHLTBaselineStatusChange";
import {
  DatepickerContainer,
  FilterContainer,
  RadioGroupContainer,
  RadioButtonContainer,
  OptionsBarContainer,
  SvgCss,
  BaselineContainer,
  BaselineInputCss,
  BaselineInputContainer,
  SaveContainer,
  BaselineButton,
  ClearButton,
  OptionButtonContainer,
  MultipleBaselineInputContainer,
  BaselineInputLabel,
  Divider,
  EnableISHLTBaselineText,
  BaselineBox,
  BaselineHeader,
  BaselineValues,
  BaselineValueRow,
  BaselineDate,
} from "./CustomOptionsBar.styles";
import { dateFilterOptions } from "./dateFilterOptions";

type CustomOptionsBarType = {
  cardTitle: string;
  onChange: (selectedDateFilter: DateFilterOptionType) => void;
  selectedFilterDate: DateFilterOptionType;
  initialOptions: ModalOptions;
  initialDisplay: string;
  initialScaleYAxis: boolean;
  showTrendLine: boolean;
  onChangeShowTrendLine: (displayValue: boolean) => void;
  onChangeOption: (options: ModalOptions) => void;
  onChangeScaleYAxis: (scaleYAxisboolean: boolean) => void;
  onDisplayChange: (filter: string) => void;
  onTableViewChange: (enableMeasurementsTable: boolean) => void;
  measurementType: string;
  mobile?: boolean;
  baselineValues?: BaselineListOptionsBar;
  baselines?: IPatientBaselineData;
  ishltBaseline?: number;
  patientId?: string;
  ishltBaselineActive?: boolean;
  onBaselineChange?: (notification: Notification) => void;
  unit?: string;
  isTableView?: boolean;
  wardISHLTBaselineActive?: boolean;
  isEmpty?: boolean;
  /**
   * The earliest point the chart actually has. "ALL" is implemented as a sentinel start date
   * (1900-01-01, see dateFilterOptions), which is fine as a query bound but meaningless to
   * read: the picker opened showing 31/12/1899. When this is given, it becomes the floor of
   * the range the picker is seeded with, so what the field shows matches the subtitle above it.
   */
  dataStartDate?: dayjs.Dayjs | null;
};

export type DateFilterOptionType = {
  /** The option id (or DATE_FILTER_CUSTOM), never the rendered label — see dateFilterOptions.ts. */
  id: string;
  endDate: dayjs.Dayjs;
  startDate: dayjs.Dayjs;
};

export function CustomOptionsBar({
  cardTitle,
  onChange,
  selectedFilterDate,
  initialOptions,
  initialDisplay,
  initialScaleYAxis,
  showTrendLine,
  onChangeShowTrendLine,
  onChangeScaleYAxis,
  onChangeOption,
  onTableViewChange,
  onDisplayChange,
  onBaselineChange,
  measurementType,
  mobile = false,
  baselineValues,
  baselines,
  ishltBaseline,
  patientId,
  ishltBaselineActive,
  unit,
  isTableView = false,
  wardISHLTBaselineActive,
  isEmpty = false,
  dataStartDate,
}: CustomOptionsBarType) {
  const { t } = useTranslation();

  /**
   * What the date picker opens on. The floor is the chart's earliest point when we know it:
   * "ALL" carries a 1900-01-01 sentinel as its start (dateFilterOptions), which is a usable
   * query bound and an unreadable thing to put in a field — it rendered as 31/12/1899.
   *
   * A clamp rather than a substitution, so it also covers the narrower filters: picking 12M on
   * a chart holding three weeks opens on the first real reading instead of a year of nothing.
   */
  const seededStartDate =
    dataStartDate && dataStartDate.isAfter(selectedFilterDate.startDate)
      ? dataStartDate
      : selectedFilterDate.startDate;

  const { unitPreferences } = useUserPreferences();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const [calendarOpen, setCalendarOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [isBaselineOpen, setIsBaselineOpen] = useState(false);
  const [isISHLTBaselineOpen, setIsISHLTBaselineOpen] = useState(false);
  const [
    isConfirmBaselineChangeModalOpen,
    setIsConfirmBaselineChangeModalOpen,
  ] = useState(false);
  const [
    isConfirmISHLTBaselineChangeModalOpen,
    setIsConfirmISHLTBaselineChangeModalOpen,
  ] = useState(false);
  const [isArchiveBaselineModalOpen, setIsArchiveBaselineModalOpen] =
    useState(false);
  const [isArchiveISHLTBaselineModalOpen, setIsArchiveISHLTBaselineModalOpen] =
    useState(false);
  const [
    isISHLTBaselineStatusUpdateModalOpen,
    setIsConfirmISHLTBaselineStatusUpdateModalOpen,
  ] = useState(false);
  const [checked, setChecked] = useState(initialDisplay);

  const [options, setOptions] = useState({
    hideFvcRejected: initialOptions?.hideFvcRejected || false,
    hideFev1Rejected: initialOptions?.hideFev1Rejected || false,
    hideFvcUsable: initialOptions?.hideFvcUsable || false,
    hideFev1Usable: initialOptions?.hideFev1Usable || false,
  });
  const [filterCount, setFilterCount] = useState(0);
  const [showMeasurementsTable, setShowMeasurementsTable] =
    useState(isTableView);

  const [ishltBaselineData, setISHLTBaselineData] =
    useState<IPatientBaseline>();
  const [baselineValue, setBaselineValue] = useState<string>("");
  const [systolicBaselineValue, setSystolicBaselineValue] =
    useState<string>("");
  const [diastolicBaselineValue, setDiastolicBaselineValue] =
    useState<string>("");
  const [ishltBaselineValue, setISHLTBaselineValue] = useState<string>("");
  const [baselineToRemove, setBaselineToRemove] = useState<string>("");

  const cardTitleId = cardTitle.replaceAll(" ", "_");
  // EN-3704: predicted FVC/FEV1 are derived from the same blow as the raw
  // value, so they are filtered by that value's quality. Undefined for the
  // measurement types that have no quality filter at all.
  const qualityFilterType = spirometryQualityFilterTypes.get(measurementType);
  const customDateFiterOption = "custom";
  const wrapperRef = useRef<any>(null);
  const filterWrapperRef = useRef<any>(null);
  const baselineWrapperRef = useRef<any>(null);
  const ishltBaselineWrapperRef = useRef<any>(null);
  const calendarButtonRef = useRef<any>(null);
  const filterButtonRef = useRef<any>(null);
  const baselineButtonRef = useRef<any>(null);
  const baselineModalRef = useRef<any>(null);
  const ishltBaselineButtonRef = useRef<any>(null);
  const ishltBaselineModalRef = useRef<any>(null);
  let dateOptions: any[] = [];

  const isFirstRender = useRef(true);

  if (mobile) {
    dateOptions = [];
  } else {
    dateOptions = dateFilterOptions;
  }

  const handleBaselineDecimalPlaces = () => {
    switch (measurementType) {
      case "fvc":
      case "calculated_predicted_fvc":
      case "fev1":
      case "calculated_predicted_fev1":
      case "pef":
      case "fev1_fvc":
      case "fev25_75":
        return 2;
      case "weight":
      case "temperature":
        return 1;
      case "height":
        if (unitPreferences?.heightUnit === "cm") return 0;
        if (unitPreferences?.heightUnit === "in") return 1;
        if (unitPreferences?.heightUnit === "ft") return 2;
        return 0;
      default:
        return 0;
    }
  };

  useEffect(() => {
    if (measurementType === "arrhythmia_status" || measurementType === "bkv") {
      onTableViewChange(true);
    }

    if (baselineValues) {
      if (baselineValues.measurementBaseline) {
        setBaselineValue(
          baselineValues.measurementBaseline !== null
            ? baselineValues.measurementBaseline.toFixed(
                handleBaselineDecimalPlaces()
              )
            : ""
        );
      }
      if (baselineValues.diastolicBaseline) {
        setDiastolicBaselineValue(
          baselineValues.diastolicBaseline !== null
            ? baselineValues.diastolicBaseline.toFixed(0)
            : ""
        );
      }
      if (baselineValues.systolicBaseline) {
        setSystolicBaselineValue(
          baselineValues.systolicBaseline !== null
            ? baselineValues.systolicBaseline.toFixed(0)
            : ""
        );
      }
    }
  }, []);

  useEffect(() => {
    if (baselines) {
      const foundIshltBaseline = baselines.baselines?.find(
        (b) => !b.archived && b.isISHLTBaseline
      );

      if (foundIshltBaseline) {
        setISHLTBaselineData(foundIshltBaseline);
        setISHLTBaselineValue(foundIshltBaseline.baseline.toFixed(2));
      } else {
        setISHLTBaselineData(undefined);
        setISHLTBaselineValue("");
      }
    } else {
      setISHLTBaselineData(undefined);
      setISHLTBaselineValue("");
    }
  }, [baselines?.activeISHLTBaseline, ishltBaselineActive]);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        calendarOpen &&
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        !calendarButtonRef.current.contains(event.target)
      ) {
        setCalendarOpen(false);
      }

      if (
        filterOpen &&
        filterWrapperRef.current &&
        !filterWrapperRef.current.contains(event.target) &&
        !filterButtonRef.current.contains(event.target)
      ) {
        setFilterOpen(false);
      }

      if (
        isBaselineOpen &&
        baselineWrapperRef.current &&
        !baselineWrapperRef.current.contains(event.target) &&
        !baselineButtonRef.current.contains(event.target)
      ) {
        if (!isArchiveBaselineModalOpen && !isConfirmBaselineChangeModalOpen) {
          if (measurementType === "bp_systolic") {
            setSystolicBaselineValue(
              baselineValues?.systolicBaseline?.toFixed(0) ?? ""
            );
            setDiastolicBaselineValue(
              baselineValues?.diastolicBaseline?.toFixed(0) ?? ""
            );
          } else {
            setBaselineValue(
              baselineValues?.measurementBaseline?.toFixed(
                handleBaselineDecimalPlaces()
              ) ?? ""
            );
          }
        }
        setIsBaselineOpen(false);
      }

      if (
        isISHLTBaselineOpen &&
        ishltBaselineWrapperRef.current &&
        !ishltBaselineWrapperRef.current.contains(event.target) &&
        !ishltBaselineButtonRef.current.contains(event.target)
      ) {
        if (!isConfirmISHLTBaselineChangeModalOpen) {
          if (ishltBaselineData) {
            setISHLTBaselineValue(ishltBaselineData.baseline.toFixed(2));
          }
        }

        setIsISHLTBaselineOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [
    calendarOpen,
    filterOpen,
    isBaselineOpen,
    isConfirmBaselineChangeModalOpen,
    isISHLTBaselineOpen,
    isConfirmISHLTBaselineChangeModalOpen,
  ]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;

      return;
    }

    onChangeOption(options);
  }, [options]);

  useEffect(() => {
    let count = 0;
    if (checked === "BestOfDay") {
      count += 1;
    }
    // Check for spirometry filters based on measurement types. PEF shows both
    // quality sections, every other type shows the one it is filtered by.
    const qualityTypes =
      measurementType === "pef" ? ["fvc", "fev1"] : [qualityFilterType];

    qualityTypes.forEach((type) => {
      if (type === "fvc") {
        if (options.hideFvcRejected) {
          count += 1;
        }
        if (options.hideFvcUsable) {
          count += 1;
        }
      }
      if (type === "fev1") {
        if (options.hideFev1Rejected) {
          count += 1;
        }
        if (options.hideFev1Usable) {
          count += 1;
        }
      }
    });

    setFilterCount(count);
  }, [checked, options]);

  const handleOnChangeDateWithDatePicker = (dateRange: any) => {
    if (dateRange.startDate && dateRange.endDate) {
      setCalendarOpen(false);
    } else {
      return;
    }

    onChange({
      id: customDateFiterOption,
      startDate: dayjs(dateRange.startDate),
      endDate: dayjs(dateRange.endDate).endOf("day"),
    });
  };

  const handleQualityFilterChangeRejected = (
    isChecked: boolean,
    type?: string
  ) => {
    // The PEF popover renders both sections, so it passes the type explicitly.
    const filterType = type ?? qualityFilterType;

    if (filterType !== "fvc" && filterType !== "fev1") return;

    mixpanelActions.track(
      `UserAction: ${
        isChecked ? "Hide" : "Unhide"
      } ${filterType.toUpperCase()} Rejected`
    );

    setOptions((prevState) => ({
      ...prevState,
      ...(filterType === "fvc"
        ? { hideFvcRejected: isChecked }
        : { hideFev1Rejected: isChecked }),
    }));
  };

  const handleQualityFilterChangeUsable = (
    isChecked: boolean,
    type?: string
  ) => {
    const filterType = type ?? qualityFilterType;

    if (filterType !== "fvc" && filterType !== "fev1") return;

    mixpanelActions.track(
      `UserAction: ${
        isChecked ? "Hide" : "Unhide"
      } ${filterType.toUpperCase()} Usable`
    );

    setOptions((prevState) => ({
      ...prevState,
      ...(filterType === "fvc"
        ? { hideFvcUsable: isChecked }
        : { hideFev1Usable: isChecked }),
    }));
  };

  const handleScaleYAxisChange = () => {
    mixpanelActions.track(`User Action: ScaleYAxis Button`);
    onChangeScaleYAxis(!initialScaleYAxis);
  };

  const handleClick = (filter: string) => {
    if (checked !== filter) {
      setChecked(filter);
      onDisplayChange(filter);
    }
  };

  const showHideToolbar = () => {
    if (spirometryMeasurementsValuesFilters.indexOf(measurementType) !== -1)
      return true;
    return false;
  };

  const getFilterCountText = () => {
    if (filterCount === 0) {
      return t("patientPage_measurementModal_optionsBar_filter");
    }
    return t("patientPage_measurementModal_optionsBar_filterCount", {
      count: filterCount,
    });
  };

  const handlePEFQualityHideFilters = () => {
    return (
      <>
        <RadioGroupContainer>
          <b>{t("patientPage_measurementModal_optionsBar_fvcQualityHide")}</b>
          <Checkbox
            checked={options.hideFvcRejected}
            onChange={(isChecked) =>
              handleQualityFilterChangeRejected(isChecked, "fvc")
            }
            label={t("patientPage_measurementModal_optionsBar_rejected")}
            id="fvc-rejected-checkbox"
            labelPosition="before"
            spacing="88px"
          />
          <Checkbox
            checked={options.hideFvcUsable}
            onChange={(isChecked) =>
              handleQualityFilterChangeUsable(isChecked, "fvc")
            }
            label={t("patientPage_measurementModal_optionsBar_usable")}
            id="fvc-usable-checkbox"
            labelPosition="before"
            spacing="96px"
          />
        </RadioGroupContainer>
        <RadioGroupContainer>
          <b>
            {t("patientPage_measurementModal_optionsBar_qualityHide", {
              type: "FEV1",
            })}
          </b>
          <Checkbox
            checked={options.hideFev1Rejected}
            onChange={(isChecked) =>
              handleQualityFilterChangeRejected(isChecked, "fev1")
            }
            label={t("patientPage_measurementModal_optionsBar_rejected")}
            id="fev1-rejected-checkbox"
            labelPosition="before"
            spacing="88px"
          />
          <Checkbox
            checked={options.hideFev1Usable}
            onChange={(isChecked) =>
              handleQualityFilterChangeUsable(isChecked, "fev1")
            }
            label={t("patientPage_measurementModal_optionsBar_usable")}
            id="fev1-usable-checkbox"
            labelPosition="before"
            spacing="96px"
          />
        </RadioGroupContainer>
      </>
    );
  };

  const handleBaselineCancel = (isISHLTBaseline: boolean) => {
    if (isISHLTBaseline) {
      setIsISHLTBaselineOpen(false);
      return;
    }

    if (measurementType === "bp_systolic") {
      setSystolicBaselineValue(
        baselineValues?.systolicBaseline?.toFixed(0) ?? ""
      );
      setDiastolicBaselineValue(
        baselineValues?.diastolicBaseline?.toFixed(0) ?? ""
      );
    } else {
      setBaselineValue(baselineValues?.measurementBaseline?.toFixed(1) ?? "");
    }
    setIsBaselineOpen(false);
  };
  const handleBaselineChange = (value: string) => {
    setBaselineValue(value);
  };
  const handleSystolicBaselineChange = (value: string) => {
    setSystolicBaselineValue(value);
  };
  const handleDiastolicBaselineChange = (value: string) => {
    setDiastolicBaselineValue(value);
  };

  const handleArchiveBaselineModalClose = async (
    confirm: boolean,
    isISHLTBaseline: boolean
  ) => {
    if (!patientId || !measurementType || !onBaselineChange) return;

    setIsArchiveBaselineModalOpen(false);

    if (confirm) {
      const parsedPatientId = parseInt(patientId, 10);
      const notification: Notification = {
        show: true,
        message: "",
        type: "success",
        width: "max-content",
      };
      if (measurementType !== "bp_systolic" && baselineValue) {
        const archiveResult = await baselinesService.archiveBaseline(
          parsedPatientId,
          measurementType,
          isISHLTBaseline
        );

        if (archiveResult.status >= 200 && archiveResult.status < 300) {
          setBaselineValue("");

          notification.message = t(
            "patientPage_measurementModal_toasts_baselineArchived"
          );
        } else {
          notification.message = t(
            "patientPage_measurementModal_toasts_genericError"
          );
          notification.type = "error";
        }
      } else {
        if (baselineToRemove === "bp_systolic") {
          const systoilicArchivedResult =
            await baselinesService.archiveBaseline(
              parsedPatientId,
              "bp_systolic",
              false
            );
          if (
            systoilicArchivedResult.status >= 200 &&
            systoilicArchivedResult.status < 300
          ) {
            setSystolicBaselineValue("");
            notification.message = t(
              "patientPage_measurementModal_toasts_baselineArchived"
            );
          } else {
            notification.message = t(
              "patientPage_measurementModal_toasts_genericError"
            );
            notification.type = "error";
          }
        }
        if (baselineToRemove === "bp_diastolic") {
          const diastolicArchivedResult =
            await baselinesService.archiveBaseline(
              parsedPatientId,
              "bp_diastolic",
              false
            );
          if (
            diastolicArchivedResult.status >= 200 &&
            diastolicArchivedResult.status < 300
          ) {
            setSystolicBaselineValue("");
            notification.message = t(
              "patientPage_measurementModal_toasts_baselineArchived"
            );
          } else {
            notification.message = t(
              "patientPage_measurementModal_toasts_genericError"
            );
            notification.type = "error";
          }
        }
      }

      onBaselineChange(notification);
    }
    setBaselineToRemove("");
  };

  const handleBaselineChangeModalClose = async (
    confirm: boolean,
    isISHLTBaseline: boolean,
    unit?: string
  ) => {
    setIsConfirmBaselineChangeModalOpen(false);
    setIsConfirmISHLTBaselineChangeModalOpen(false);

    if (!patientId || !measurementType || !onBaselineChange) return;

    const parsedPatientId = parseInt(patientId, 10);
    const notification: Notification = {
      show: true,
      message: "",
      type: "success",
      width: "max-content",
    };

    if (confirm) {
      const baselineToAdd = isISHLTBaseline
        ? ishltBaselineValue
        : baselineValue;

      if (measurementType !== "bp_systolic" && baselineToAdd) {
        const newBaseline = parseFloat(baselineToAdd);

        const request: IAddBaseline = {
          patientId: parsedPatientId,
          measurementType,
          baseline: newBaseline,
          isISHLTBaseline,
          unit,
        };

        const addResult = await baselinesService.addBaseline(request);

        if (addResult.status >= 200 && addResult.status < 300) {
          notification.message = t(
            "patientPage_measurementModal_toasts_baselineAdded"
          );
        } else {
          notification.message = t(
            "patientPage_measurementModal_toasts_genericError"
          );
          notification.type = "error";
        }
      } else {
        const newSystolicBaseline = parseFloat(systolicBaselineValue);
        const newDiastolicBaseline = parseFloat(diastolicBaselineValue);

        let systolicStatus = true;
        let diastolicStatus = true;

        if (
          newSystolicBaseline &&
          newSystolicBaseline !== baselineValues?.systolicBaseline
        ) {
          const systolicRequest: IAddBaseline = {
            patientId: parsedPatientId,
            measurementType: "bp_systolic",
            baseline: newSystolicBaseline,
            isISHLTBaseline: false,
            unit,
          };

          const addSystolicResult = await baselinesService.addBaseline(
            systolicRequest
          );
          systolicStatus =
            addSystolicResult.status >= 200 && addSystolicResult.status < 300;
        }

        if (
          newDiastolicBaseline &&
          newDiastolicBaseline !== baselineValues?.diastolicBaseline
        ) {
          const diastolicRequest: IAddBaseline = {
            patientId: parsedPatientId,
            measurementType: "bp_diastolic",
            baseline: newDiastolicBaseline,
            isISHLTBaseline: false,
            unit,
          };

          const addDiastolicResult = await baselinesService.addBaseline(
            diastolicRequest
          );

          diastolicStatus =
            addDiastolicResult.status >= 200 && addDiastolicResult.status < 300;
        }

        if (systolicStatus && diastolicStatus) {
          notification.message = t(
            "patientPage_measurementModal_toasts_baselinesAdded"
          );
        } else {
          notification.message = t(
            "patientPage_measurementModal_toasts_genericError"
          );
          notification.type = "error";
        }
      }

      onBaselineChange(notification);
    } else {
      setSystolicBaselineValue(
        baselineValues?.systolicBaseline && baselineValues
          ? baselineValues?.systolicBaseline?.toFixed(0)
          : ""
      );
      setDiastolicBaselineValue(
        baselineValues?.diastolicBaseline && baselineValues
          ? baselineValues.diastolicBaseline.toFixed(0)
          : ""
      );
    }
  };

  const checkBaselineEmptyValues = () => {
    if (measurementType === "bp_systolic") {
      return diastolicBaselineValue === "" && systolicBaselineValue === "";
    }
    return baselineValue === "";
  };

  const getOldBaselineValues = (isISHLTBaseline: boolean) => {
    if (isISHLTBaseline) {
      return (ishltBaselineData?.baseline ?? 0).toString();
    }

    if (measurementType === "bp_systolic") {
      if (
        !baselineValues?.systolicBaseline &&
        !baselineValues?.diastolicBaseline
      ) {
        return t("patientPage_measurementModal_optionsBar_notSet");
      }
      return `${baselineValues?.systolicBaseline ?? "-"}/${
        baselineValues?.diastolicBaseline ?? "-"
      }`;
    }

    return (baselineValues?.measurementBaseline ?? 0).toString();
  };

  const getNewBaselineValues = (isISHLTBaseline: boolean) => {
    if (isISHLTBaseline) {
      return (ishltBaselineValue ?? 0).toString();
    }

    if (measurementType === "bp_systolic") {
      return `${systolicBaselineValue !== "" ? systolicBaselineValue : "-"}/${
        diastolicBaselineValue !== "" ? diastolicBaselineValue : "-"
      }`;
    }
    return (baselineValue ?? 0).toString();
  };

  const getBaselineButtonLabel = () => {
    if (measurementType === "bp_systolic") {
      const systolicValue = baselineValues?.systolicBaseline?.toFixed(0);
      const diastolicValue = baselineValues?.diastolicBaseline?.toFixed(0);
      if (systolicValue || diastolicValue) {
        return t("patientPage_measurementModal_optionsBar_baselinePair", {
          systolic: systolicValue ?? "-",
          diastolic: diastolicValue ?? "-",
        });
      }
      return t("patientPage_measurementModal_optionsBar_addBaselines");
    }

    return baselineValues?.measurementBaseline
      ? t("patientPage_measurementModal_optionsBar_baselineValue", {
          value: baselineValues.measurementBaseline.toFixed(
            handleBaselineDecimalPlaces()
          ),
        })
      : t("patientPage_measurementModal_optionsBar_addBaseline");
  };

  const getISHLTBaselineButtonLabel = () => {
    return ishltBaseline && ishltBaselineActive
      ? t("patientPage_measurementModal_optionsBar_ishltBaselineValue", {
          value: ishltBaseline.toFixed(2),
        })
      : t("patientPage_measurementModal_optionsBar_addIshltBaseline");
  };

  const handleISHLTStatusUpdateModalClose = async (confirm: boolean) => {
    if (confirm) {
      if (!patientId || !measurementType) return;

      const result = await baselinesService.updateISHLTBaselineEnabledStatus(
        parseInt(patientId, 10),
        measurementType
      );

      if (result.status >= 200 && result.status < 300) {
        // Invalidate patient info query to trigger re-fetch with updated ishltBaselineActive
        await queryClient.invalidateQueries({
          queryKey: [`patient-info-${patientId}`],
        });

        if (!ishltBaselineActive) {
          showToast(
            t("patientPage_measurementModal_toasts_ishltOn"),
            "success"
          );
        } else {
          showToast(
            t("patientPage_measurementModal_toasts_ishltOff"),
            "success"
          );
        }
      } else {
        showToast(
          t("patientPage_measurementModal_toasts_genericError"),
          "error"
        );
      }
    }

    setIsConfirmISHLTBaselineStatusUpdateModalOpen(false);
  };

  const getISHLTBaselineTooltipTitle = () => {
    if (
      ishltBaselineData &&
      ishltBaselineData.lowerCi &&
      ishltBaselineData.upperCi &&
      ishltBaselineData.firstTestTime &&
      ishltBaselineData.lastTestTime
    ) {
      return (
        <BaselineBox>
          <BaselineHeader>
            <span>{t("chart_baselineTooltip_ishlt")}</span>
            <span>{ishltBaseline?.toFixed(2)}</span>
          </BaselineHeader>
          <BaselineDate>
            <span>
              {ishltBaselineData?.created
                ? getLongFormattedDate(ishltBaselineData.created)
                : "-"}
            </span>
          </BaselineDate>

          <BaselineValues>
            <span>{t("chart_baselineTooltip_valuesUsed")}</span>

            <BaselineValueRow key={ishltBaselineData.lastTestTime.toString()}>
              <span>{ishltBaselineData.upperCi?.toFixed(2)}</span>
              <span>
                – {getShortFormattedDate(ishltBaselineData.lastTestTime)}
              </span>
            </BaselineValueRow>

            <BaselineValueRow key={ishltBaselineData.firstTestTime.toString()}>
              <span>{ishltBaselineData.lowerCi?.toFixed(2)}</span>
              <span>
                – {getShortFormattedDate(ishltBaselineData.firstTestTime)}
              </span>
            </BaselineValueRow>
          </BaselineValues>
        </BaselineBox>
      );
    }

    return (
      <BaselineBox>
        <BaselineHeader>
          <span>{t("chart_baselineTooltip_ishlt")}</span>
          <span>{ishltBaseline?.toFixed(2)}</span>
        </BaselineHeader>
        <BaselineDate>
          <span>
            {ishltBaselineData?.created
              ? getLongFormattedDate(ishltBaselineData.created)
              : "-"}
          </span>
        </BaselineDate>
      </BaselineBox>
    );
  };

  return (
    <OptionsBarContainer>
      <OptionsBar>
        <div
          style={{ backgroundColor: "white", borderRadius: "8px 0 0 8px" }}
          ref={calendarButtonRef}
        >
          <OptionButton
            key={`${cardTitleId}_customDate`}
            text={t("patientPage_measurementModal_optionsBar_custom")}
            icon={<Calendar />}
            selected={selectedFilterDate.id === customDateFiterOption}
            onClick={() => {
              mixpanelActions.track(`User Action: CustomDateFilter`);
              setCalendarOpen((prevState) => !prevState);
            }}
          />
        </div>

        {dateOptions.map((dateFilterOption, index) => {
          return (
            <OptionButton
              key={`${cardTitleId}_${dateFilterOption.id}_${index}`}
              text={t(dateFilterOption.labelKey)}
              selected={dateFilterOption.id === selectedFilterDate.id}
              onClick={() => {
                mixpanelActions.track(
                  `User Action: Changed Date Range ${dateFilterOption.id}`
                );

                const currentDate = dayjs();
                onChange({
                  endDate: currentDate,
                  id: dateFilterOption.id,
                  startDate: dateFilterOption.value(currentDate),
                });
              }}
            />
          );
        })}
      </OptionsBar>
      {measurementType !== "arrhythmia_status" && measurementType !== "bkv" ? (
        <>
          <div>
            <OptionsBar borderRadius="16px">
              <OptionButton
                borderRadius="11px 0 0 11px"
                selected={!showMeasurementsTable}
                disabled={isEmpty}
                className={SvgCss()}
                text=""
                onClick={() => {
                  setShowMeasurementsTable(false);
                  onTableViewChange(false);
                }}
                icon={<GraphToggle />}
              />
              <OptionButton
                borderRadius="0 11px 11px 0"
                selected={showMeasurementsTable}
                disabled={isEmpty}
                className={SvgCss()}
                text=""
                onClick={() => {
                  setShowMeasurementsTable(true);
                  onTableViewChange(true);
                }}
                icon={<TableToggle />}
              />
            </OptionsBar>
          </div>
          {measurementType !== "nebulizer" ? (
            <>
              <OptionsBar
                border={
                  filterCount > 0
                    ? "1px solid var(--colors-measurement-modal-filter-button-active-border)"
                    : undefined
                }
                borderRadius="16px"
              >
                <div
                  style={{ backgroundColor: "white", borderRadius: "16px" }}
                  ref={filterButtonRef}
                >
                  <OptionButton
                    key={`${cardTitleId}_filter`}
                    text={getFilterCountText()}
                    backgroundColor={
                      filterCount > 0
                        ? "var(--colors-measurement-modal-filter-button-active-background)"
                        : undefined
                    }
                    color={
                      filterCount > 0
                        ? "var(--colors-measurement-modal-filter-button-active-text)"
                        : undefined
                    }
                    borderRadius="16px"
                    letterSpacing="0.1px"
                    padding="2px 12px"
                    hoverBackgroundColor={
                      filterCount > 0
                        ? "var(--colors-measurement-modal-filter-button-active-hover-background)"
                        : undefined
                    }
                    onClick={() => {
                      mixpanelActions.track(`User Action: FilterButtonClicked`);
                      setFilterOpen((prevState) => !prevState);
                    }}
                  />
                </div>
              </OptionsBar>
              <OptionsBar borderRadius="16px">
                <OptionButtonContainer ref={filterButtonRef}>
                  <OptionButton
                    key={`${cardTitleId}_scaleY`}
                    text={
                      initialScaleYAxis
                        ? t(
                            "patientPage_measurementModal_optionsBar_unscaleYAxis"
                          )
                        : t(
                            "patientPage_measurementModal_optionsBar_scaleYAxis"
                          )
                    }
                    selected={initialScaleYAxis}
                    disabled={isEmpty}
                    borderRadius="16px"
                    letterSpacing="0.1px"
                    padding="2px 12px"
                    onClick={() => {
                      handleScaleYAxisChange();
                    }}
                  />
                </OptionButtonContainer>
              </OptionsBar>
              <OptionsBar borderRadius="16px">
                <OptionButtonContainer
                  ref={baselineButtonRef}
                  hasBaseline={baselineValues !== undefined}
                >
                  <OptionButton
                    key={`${cardTitleId}_baseline`}
                    text={getBaselineButtonLabel()}
                    selected={isBaselineOpen}
                    borderRadius="16px"
                    letterSpacing="0.1px"
                    padding="2px 12px"
                    onClick={() => {
                      mixpanelActions.track(
                        "User Action: BaselineButtonClicked"
                      );
                      setIsBaselineOpen((prevState) => !prevState);
                    }}
                  />
                </OptionButtonContainer>
              </OptionsBar>
              {wardISHLTBaselineActive && measurementType === "fev1" ? (
                <OptionsBar borderRadius="16px">
                  <Tooltip
                    title={
                      ishltBaselineData
                        ? getISHLTBaselineTooltipTitle()
                        : undefined
                    }
                    zIndex={3002}
                    color="#4B4B4B"
                    arrow={false}
                    styles={{
                      body: {
                        borderRadius: "4px !important",
                        padding: "12px 32px",
                      },
                    }}
                  >
                    <OptionButtonContainer
                      ref={ishltBaselineButtonRef}
                      hasBaseline={wardISHLTBaselineActive}
                    >
                      <OptionButton
                        key={`${cardTitleId}_ishlt_baseline`}
                        text={getISHLTBaselineButtonLabel()}
                        selected={isBaselineOpen}
                        borderRadius="16px"
                        letterSpacing="0.1px"
                        padding="2px 12px"
                        onClick={() => {
                          mixpanelActions.track(
                            "User Action: ISHLTBaselineButtonClicked"
                          );
                          setIsISHLTBaselineOpen((prevState) => !prevState);
                        }}
                      />
                    </OptionButtonContainer>
                  </Tooltip>
                </OptionsBar>
              ) : null}
            </>
          ) : null}
        </>
      ) : (
        <div>
          {measurementType === "arrhythmia_status" ? (
            <OptionsBar borderRadius="16px">
              <OptionButtonContainer>
                <OptionButton
                  key="fibricheck_button"
                  text={t(
                    "patientPage_measurementModal_optionsBar_openFibricheck"
                  )}
                  borderRadius="16px"
                  letterSpacing="0.1px"
                  padding="2px 12px"
                  onClick={() => {
                    window.open(
                      process.env.REACT_APP_FIBRICHECK_PORTAL,
                      "_blank"
                    );
                  }}
                />
              </OptionButtonContainer>
            </OptionsBar>
          ) : (
            ""
          )}
        </div>
      )}

      {calendarOpen && (
        <DatepickerContainer ref={wrapperRef}>
          <DateRangePicker
            initialDateRange={{
              startDate: seededStartDate.toDate(),
              endDate: selectedFilterDate.endDate.endOf("day").toDate(),
            }}
            onDateChange={handleOnChangeDateWithDatePicker}
          />
        </DatepickerContainer>
      )}

      {filterOpen && (
        <FilterContainer ref={filterWrapperRef}>
          <RadioGroupContainer>
            <b>{t("patientPage_measurementModal_optionsBar_dataDisplay")}</b>
            <RadioButtonContainer>
              <p>{t("patientPage_measurementModal_optionsBar_allPoints")}</p>
              <RadioButton
                checked={checked === ""}
                onClick={() => handleClick("")}
              />
            </RadioButtonContainer>
            <RadioButtonContainer>
              <p>{t("patientPage_measurementModal_optionsBar_highestOfDay")}</p>
              <RadioButton
                checked={checked === "BestOfDay"}
                onClick={() => handleClick("BestOfDay")}
              />
            </RadioButtonContainer>
            <Divider />
            <b>{t("patientPage_measurementModal_optionsBar_graphElements")}</b>
            <RadioButtonContainer>
              <p>{t("patientPage_measurementModal_optionsBar_trendLine")}</p>
              <RadioButton
                checked={showTrendLine}
                onClick={() => onChangeShowTrendLine(!showTrendLine)}
              />
            </RadioButtonContainer>
          </RadioGroupContainer>
          {showHideToolbar() && (
            <RadioGroupContainer>
              <Divider />
              <b>
                {t("patientPage_measurementModal_optionsBar_qualityHide", {
                  type: qualityFilterType?.toUpperCase(),
                })}
              </b>
              <Checkbox
                checked={
                  qualityFilterType === "fvc"
                    ? options.hideFvcRejected
                    : options.hideFev1Rejected
                }
                onChange={handleQualityFilterChangeRejected}
                label={t("patientPage_measurementModal_optionsBar_rejected")}
                id={`${measurementType}-rejected-checkbox`}
                labelPosition="before"
                spacing="88px"
              />
              <Checkbox
                checked={
                  qualityFilterType === "fvc"
                    ? options.hideFvcUsable
                    : options.hideFev1Usable
                }
                onChange={handleQualityFilterChangeUsable}
                label={t("patientPage_measurementModal_optionsBar_usable")}
                id={`${measurementType}-usable-checkbox`}
                labelPosition="before"
                spacing="96px"
              />
            </RadioGroupContainer>
          )}
          {measurementType === "pef" && handlePEFQualityHideFilters()}
        </FilterContainer>
      )}

      {isBaselineOpen && (
        <BaselineContainer
          isLarge={measurementType === "bp_systolic"}
          ref={baselineWrapperRef}
        >
          {measurementType !== "bp_systolic" ? (
            <BaselineInputContainer>
              <p>{t("patientPage_measurementModal_optionsBar_value")}</p>
              <TextField
                label=""
                placeholder="0"
                backgroudColor="white "
                value={baselineValue}
                className={BaselineInputCss()}
                onChange={(event) => handleBaselineChange(event.target.value)}
                type="number"
                maxDecimalPlaces={handleBaselineDecimalPlaces()}
              />
              {baselineValues?.measurementBaseline ? (
                <ClearButton
                  onClick={() => {
                    setIsArchiveBaselineModalOpen(true);
                  }}
                >
                  <CircleX />
                </ClearButton>
              ) : null}
            </BaselineInputContainer>
          ) : (
            <MultipleBaselineInputContainer>
              <BaselineInputContainer>
                <BaselineInputLabel>
                  {t("patientPage_measurementModal_optionsBar_systolic")}
                </BaselineInputLabel>
                <TextField
                  label=""
                  placeholder="0"
                  backgroudColor="white "
                  value={systolicBaselineValue}
                  className={BaselineInputCss()}
                  onChange={(event) =>
                    handleSystolicBaselineChange(event.target.value)
                  }
                  type="number"
                />
                {baselineValues?.systolicBaseline ? (
                  <ClearButton
                    onClick={() => {
                      setIsArchiveBaselineModalOpen(true);
                      setBaselineToRemove("bp_systolic");
                    }}
                  >
                    <CircleX />
                  </ClearButton>
                ) : null}
              </BaselineInputContainer>
              <BaselineInputContainer>
                <BaselineInputLabel>
                  {t("patientPage_measurementModal_optionsBar_diastolic")}
                </BaselineInputLabel>
                <TextField
                  label=""
                  placeholder="0"
                  backgroudColor="white "
                  value={diastolicBaselineValue}
                  className={BaselineInputCss()}
                  onChange={(event) =>
                    handleDiastolicBaselineChange(event.target.value)
                  }
                  type="number"
                />
                {baselineValues?.diastolicBaseline ? (
                  <ClearButton
                    onClick={() => {
                      setIsArchiveBaselineModalOpen(true);
                      setBaselineToRemove("bp_diastolic");
                    }}
                  >
                    <CircleX />
                  </ClearButton>
                ) : null}
              </BaselineInputContainer>
            </MultipleBaselineInputContainer>
          )}

          <SaveContainer>
            <BaselineButton onClick={() => handleBaselineCancel(false)}>
              {t("patientPage_measurementModal_baseline_cancel")}
            </BaselineButton>
            <BaselineButton
              isSave
              isDisabled={checkBaselineEmptyValues()}
              onClick={() => {
                setIsConfirmBaselineChangeModalOpen(true);
              }}
            >
              {t("patientPage_measurementModal_baseline_save")}
            </BaselineButton>
          </SaveContainer>
        </BaselineContainer>
      )}

      {isISHLTBaselineOpen && (
        <BaselineContainer
          hasISHLTText={wardISHLTBaselineActive}
          ref={ishltBaselineWrapperRef}
        >
          <BaselineInputContainer>
            <p>{t("patientPage_measurementModal_optionsBar_value")}</p>
            <TextField
              label=""
              placeholder="0"
              backgroudColor="white "
              value={ishltBaselineValue}
              className={BaselineInputCss()}
              onChange={(event) => setISHLTBaselineValue(event.target.value)}
              type="number"
              maxDecimalPlaces={2}
            />
            <EnableISHLTBaselineText
              onClick={() =>
                setIsConfirmISHLTBaselineStatusUpdateModalOpen(true)
              }
            >
              {t(
                ishltBaselineActive
                  ? "patientPage_measurementModal_baseline_turnOffIshlt"
                  : "patientPage_measurementModal_baseline_turnOnIshlt"
              )}
            </EnableISHLTBaselineText>
            {ishltBaselineData?.baseline ? (
              <ClearButton
                onClick={() => {
                  setIsArchiveISHLTBaselineModalOpen(true);
                }}
              >
                <CircleX />
              </ClearButton>
            ) : null}
          </BaselineInputContainer>

          <SaveContainer hasISHLTEnableText={wardISHLTBaselineActive}>
            <BaselineButton onClick={() => handleBaselineCancel(true)}>
              {t("patientPage_measurementModal_baseline_cancel")}
            </BaselineButton>
            <BaselineButton
              isSave
              isDisabled={ishltBaselineValue === ""}
              onClick={() => {
                setIsConfirmISHLTBaselineChangeModalOpen(true);
              }}
            >
              {t("patientPage_measurementModal_baseline_save")}
            </BaselineButton>
          </SaveContainer>
        </BaselineContainer>
      )}

      {isConfirmBaselineChangeModalOpen ? (
        <div ref={baselineModalRef}>
          <ConfirmBaselineChangeModal
            oldBaseline={getOldBaselineValues(false)}
            newBaseline={getNewBaselineValues(false)}
            unit={unit}
            isISHLTBaseline={false}
            measurementType={measurementType}
            onClose={handleBaselineChangeModalClose}
          />
        </div>
      ) : null}

      {isConfirmISHLTBaselineChangeModalOpen ? (
        <div ref={baselineModalRef}>
          <ConfirmBaselineChangeModal
            oldBaseline={getOldBaselineValues(true)}
            newBaseline={getNewBaselineValues(true)}
            unit={unit}
            measurementType={measurementType}
            isISHLTBaseline
            onClose={handleBaselineChangeModalClose}
          />
        </div>
      ) : null}

      {isISHLTBaselineStatusUpdateModalOpen ? (
        <div ref={ishltBaselineModalRef}>
          <ConfirmISHLTBaselineStatusChange
            ishltEnabledStatus={ishltBaselineActive ?? false}
            onClose={handleISHLTStatusUpdateModalClose}
          />
        </div>
      ) : null}

      {isArchiveBaselineModalOpen && baselineValues ? (
        <ArchiveBaselineModal
          baseline={baselineValues?.measurementBaseline ?? 0}
          unit={unit}
          isISHLTBaseline={false}
          decimalPlaces={handleBaselineDecimalPlaces()}
          onClose={handleArchiveBaselineModalClose}
        />
      ) : null}

      {isArchiveISHLTBaselineModalOpen && ishltBaselineData ? (
        <ArchiveBaselineModal
          baseline={ishltBaselineData.baseline ?? 0}
          unit={unit}
          isISHLTBaseline
          decimalPlaces={handleBaselineDecimalPlaces()}
          onClose={handleArchiveBaselineModalClose}
        />
      ) : null}
    </OptionsBarContainer>
  );
}
